import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { API, getAuthHeaders } from "@/lib/api";
import { toast } from "sonner";
import { Spinner, CaretDown, CaretUp } from "@phosphor-icons/react";

const KIND_TO_SECTION = {
  series: "series",
  auto_create: "auto-create",
  bulk: "bulk-create",
  lifestyle: "lifestyle",
};

const KIND_LABEL = {
  series: "Series Studio",
  auto_create: "Auto Create",
  bulk: "Bulk Create",
  lifestyle: "Lifestyle",
};

// Persistent, cross-section render tracker. Mounted once at the Dashboard level
// so renders started in one section keep showing live progress no matter where
// the user navigates. Backend tasks already run independently; this polls the
// aggregated /render-status read model.
export const GlobalRenderTracker = ({ onNavigate }) => {
  const [active, setActive] = useState([]);
  const [open, setOpen] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const seenActive = useRef(new Set());
  const notified = useRef(new Set());

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/render-status`, { headers: getAuthHeaders() });
        if (!r.ok) return;
        const d = await r.json();
        const nowActive = d.active || [];
        if (!stop) {
          setActive(nowActive);
          if (nowActive.length > 0) setDismissed(false);
        }

        // Track which ids are currently rendering.
        const activeIds = new Set(nowActive.map((x) => x.id));
        nowActive.forEach((x) => seenActive.current.add(x.id));

        // Anything we saw active that now shows up as recent/complete → notify.
        (d.recent || []).forEach((r2) => {
          if (
            seenActive.current.has(r2.id) &&
            !activeIds.has(r2.id) &&
            !notified.current.has(r2.id)
          ) {
            notified.current.add(r2.id);
            const failed = (r2.status || "").toLowerCase().includes("fail");
            const section = KIND_TO_SECTION[r2.kind];
            if (failed) {
              toast.error(`${KIND_LABEL[r2.kind] || "Render"} failed: ${r2.label}`, {
                description: (r2.error || "").slice(0, 140) || undefined,
              });
            } else {
              toast.success(`${KIND_LABEL[r2.kind] || "Render"} ready: ${r2.label}`, {
                action: section
                  ? { label: "View", onClick: () => onNavigate && onNavigate(section) }
                  : undefined,
              });
            }
          }
        });
      } catch {
        /* non-fatal */
      }
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [onNavigate]);

  if (active.length === 0 || dismissed) return null;

  const node = (
    <div
      className="fixed bottom-24 right-4 sm:right-6 z-[9998] w-[290px] max-w-[calc(100vw-2rem)]"
      data-testid="global-render-tracker"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden">
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="render-tracker-toggle"
          className="w-full flex items-center gap-2 px-4 py-3 bg-zinc-900 text-white"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E91E8C] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#E91E8C]" />
          </span>
          <span className="text-sm font-semibold flex-1 text-left">
            {active.length} render{active.length > 1 ? "s" : ""} in progress
          </span>
          {open ? <CaretDown size={16} /> : <CaretUp size={16} />}
        </button>
        {open && (
          <div className="max-h-[320px] overflow-y-auto divide-y divide-zinc-100">
            {active.map((r) => {
              const section = KIND_TO_SECTION[r.kind];
              return (
                <button
                  key={r.id}
                  onClick={() => section && onNavigate && onNavigate(section)}
                  data-testid={`render-tracker-item-${r.id}`}
                  className="w-full flex items-start gap-3 px-4 py-3 hover:bg-zinc-50 text-left transition-colors"
                >
                  <Spinner size={18} className="text-[#E91E8C] animate-spin mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-900 truncate">{r.label}</p>
                    <p className="text-xs text-zinc-500 truncate">
                      {(KIND_LABEL[r.kind] || r.kind)} · {r.sublabel || r.step}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default GlobalRenderTracker;
